import React from 'react';
import { Loader2 } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';

// Toutes les variables des 9 emails superadmin réunies, disponibles dans chaque bloc (même principe que
// côté client : le serveur calcule toujours l'intégralité du jeu de variables, celles qui ne concernent
// pas l'email en cours restent simplement vides).
const ALL_ADMIN_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom établissement' },
    { token: '{{client_nom}}', label: 'Nom / contact client' },
    { token: '{{identifiant}}', label: 'Identifiant' },
    { token: '{{mot_de_passe}}', label: 'Mot de passe' },
    { token: '{{collaborateur_nom}}', label: 'Nom du collaborateur' },
    { token: '{{salarie_prenom}}', label: 'Prénom du salarié' },
    { token: '{{salarie_nom}}', label: 'Nom du salarié' },
    { token: '{{motif}}', label: 'Motif' },
    { token: '{{message}}', label: 'Message' },
    { token: '{{lien}}', label: 'Lien' },
    { token: '{{type_client}}', label: 'Type de client' },
];

const SENT_EMAIL_TYPES: { key: string; title: string; description: string }[] = [
    { key: 'client_create', title: 'Création de compte client', description: "Envoyé à la création d'un nouveau client (bouton \"Ajouter un client\")." },
    { key: 'client_reset_password', title: 'Réinitialisation mot de passe client', description: "Envoyé quand le client (ou vous, à sa demande) réinitialise son propre mot de passe — bouton \"Réinitialiser\"." },
    { key: 'client_force_reset_password', title: 'Mot de passe client réinitialisé de force', description: "Envoyé quand vous forcez la réinitialisation du mot de passe d'un client sans qu'il l'ait demandé — bouton \"Forcer la réinitialisation\", ton sécurité distinct du mail normal." },
    { key: 'collaborator_credentials', title: 'Identifiants collaborateur', description: "Envoyé à la création d'un collaborateur, à l'acceptation d'une demande de collaborateur, ou au reset normal de son mot de passe." },
    { key: 'collaborator_force_reset_password', title: 'Mot de passe collaborateur réinitialisé de force', description: "Envoyé quand vous forcez la réinitialisation du mot de passe d'un collaborateur — bouton \"Forcer la réinitialisation\", ton sécurité distinct du mail d'identifiants normal." },
    { key: 'collaborator_request_refuse', title: 'Refus de demande de collaborateur', description: "Envoyé au client lorsqu'une demande de création de collaborateur est refusée." },
    { key: 'employee_reset_password', title: 'Réinitialisation mot de passe salarié', description: "Envoyé lors du reset du mot de passe portail d'un salarié." },
    { key: 'support_reply', title: 'Réponse à un ticket support', description: 'Envoyé au client lorsque vous répondez à un message dans le support admin.' },
    { key: 'registration_invite', title: "Invitation à s'inscrire", description: "Envoyé lors de l'envoi d'un formulaire d'inscription à un futur client." },
    { key: 'registration_accept', title: "Acceptation de la demande d'inscription", description: "Envoyé lorsque vous acceptez une demande d'inscription (identifiants de connexion)." },
    { key: 'registration_refuse', title: "Refus de la demande d'inscription", description: "Envoyé lorsque vous refusez une demande d'inscription." },
];

const RECEIVED_EMAIL_TYPES: { key: string; title: string; description: string }[] = [
    { key: 'registration_submitted', title: "Nouvelle demande d'inscription soumise", description: "Reçu lorsqu'un prospect a rempli et envoyé son formulaire d'inscription." },
    { key: 'support_new_message', title: 'Nouveau message support', description: 'Reçu lorsqu\'un client envoie un message dans le support.' },
    { key: 'signature_digitize_request', title: 'Demande de numérisation de signature', description: "Reçu lorsqu'un client ou un salarié soumet une photo de signature à numériser." },
    { key: 'collaborator_request_new', title: 'Nouvelle demande de collaborateur', description: 'Reçu lorsqu\'un client soumet une demande de création de compte collaborateur.' },
    { key: 'exception_service_automation_request', title: "Demande d'automatisation Service d'exception", description: "Reçu lorsqu'un client demande la connexion d'une plateforme de réservation (Zenchef...) pour automatiser la saisie des réservations." },
];

/** Un bloc autonome (garde ses propres refs subject/body, stables pour toute sa durée de vie) — évite
 *  le piège d'une map de refs dynamique qui ne s'attacherait jamais réellement aux éléments DOM. */
const AdminEmailTypeBlock: React.FC<{
    title: string;
    description: string;
    subject: string;
    body: string;
    onChangeSubject: (v: string) => void;
    onChangeBody: (v: string) => void;
}> = ({ title, description, subject, body, onChangeSubject, onChangeBody }) => {
    const [activeField, setActiveField] = React.useState<'subject' | 'body'>('body');
    const subjectRef = React.useRef<HTMLInputElement>(null);
    const bodyRef = React.useRef<HTMLTextAreaElement>(null);

    const insertToken = (token: string) => {
        if (activeField === 'subject') insertNotificationToken(subjectRef.current, subject, onChangeSubject, token);
        else insertNotificationToken(bodyRef.current, body, onChangeBody, token);
    };
    const wrapBody = (marker: string) => wrapNotificationSelection(bodyRef.current, body, onChangeBody, marker);

    return (
        <div className="space-y-1.5">
            <p className="text-xs text-content-muted italic">{description}</p>
            <NotificationBlock
                title={title}
                subject={subject} setSubject={onChangeSubject}
                body={body} setBody={onChangeBody}
                subjectRef={subjectRef} bodyRef={bodyRef}
                activeField={activeField} setActiveField={setActiveField}
                onInsertToken={insertToken} onWrap={wrapBody}
                tokens={ALL_ADMIN_TOKENS}
            />
        </div>
    );
};

export const AdminNotificationSettings: React.FC = () => {
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        adminApi.getAdminNotificationSettings().then((s: any) => setValues(s || {})).catch((e) => console.error('Failed to load admin notification settings', e)).finally(() => setLoading(false));
    }, []);

    const setField = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const result: any = await adminApi.saveAdminNotificationSettings(values);
            if (result) setValues(result);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save admin notification settings', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>;
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <button onClick={handleSave} disabled={saving}
                className="fixed bottom-6 right-6 z-30 px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold shadow-2xl shadow-black/30 disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>

            <h2 className="text-xs font-black uppercase tracking-wider text-content-muted">Emails que vous envoyez</h2>
            {SENT_EMAIL_TYPES.map((t) => (
                <AdminEmailTypeBlock
                    key={t.key}
                    title={t.title}
                    description={t.description}
                    subject={values[`${t.key}_subject`] || ''}
                    body={values[`${t.key}_body`] || ''}
                    onChangeSubject={(v) => setField(`${t.key}_subject`, v)}
                    onChangeBody={(v) => setField(`${t.key}_body`, v)}
                />
            ))}

            <h2 className="text-xs font-black uppercase tracking-wider text-content-muted pt-4">Emails que vous recevez</h2>
            {RECEIVED_EMAIL_TYPES.map((t) => (
                <AdminEmailTypeBlock
                    key={t.key}
                    title={t.title}
                    description={t.description}
                    subject={values[`${t.key}_subject`] || ''}
                    body={values[`${t.key}_body`] || ''}
                    onChangeSubject={(v) => setField(`${t.key}_subject`, v)}
                    onChangeBody={(v) => setField(`${t.key}_body`, v)}
                />
            ))}
        </div>
    );
};
